<template>
  <v-container>
    <v-card>
      <v-card-title>
        <span class="headline">Driver pay-in report</span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="fmtFromDate"
                  label="From date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  dense
                  clearable
                  @click:clear="searchCriteria.fromDate = null"></v-text-field>
              </template>
              <v-date-picker
                v-model="searchCriteria.fromDate"
                @input="fromDateMenu=false;setEndOfMonth();">
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col>
            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="fmtToDate"
                  label="To date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  dense
                  clearable
                  @click:clear="searchCriteria.toDate = null"></v-text-field>
              </template>
              <v-date-picker
                v-model="searchCriteria.toDate"
                @input="toDateMenu=false">
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row v-if="!IS_CONTRACT_DRIVER">
          <v-col>
            <v-select
              :items="USERS_BY_ROLE"
              :item-text="item => item.givenName + ' ' + item.familyName"
              item-value="id"
              label="Driver"
              outlined
              clearable
              v-model="searchCriteria.driverId">
            </v-select>
          </v-col>
        </v-row>
        <v-row class="px-4">
          <v-btn color="primary" class="d-print-none" @click="runReport()">Run Report</v-btn>
        </v-row>
      </v-card-text>
    </v-card>
    <v-row class="pt-8">
      <v-container fluid fill-height>
        <v-card width="100vw">
          <v-data-table
              dense
              hide-default-footer
              disable-pagination
              :headers="headers"
              group-by="driverId"
              :items="reportData.items">
              <template v-slot:item.cashAmount="{ item }">
                <span>{{item.cashAmount | toCurrency}}</span>
              </template>
              <template v-slot:item.creditCardAmount="{ item }">
                <span>{{item.creditCardAmount | toCurrency}}</span>
              </template>
              <template v-slot:item.accountAmount="{ item }">
                <span>{{item.accountAmount | toCurrency}}</span>
              </template>
              <template v-slot:group.header="{items, isOpen, toggle}">
                <th class="red lighten-4">
                  <v-icon @click="toggle">{{ isOpen ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
                </th>
                <th colspan="6" class="red lighten-4">
                  <span>Driver: {{ items[0].driverName }}</span>
                </th>
              </template>
              <template v-slot:item.driverName="{ item }">
                {{item.driverName}}
                <span class="red--text darken-4 font-weight-bold">
                  {{ showPercentageNotFoundMark(item) }}
                </span>
              </template>
              <template v-slot:group.summary="{ items }">
                <th colspan="3">Gross turnover (Driver)</th>
                <th class="text-right">Total: {{ driverGrossTotal(items) | toCurrency }}</th>
                <th class="text-right">
                  {{ cashTotal(items) | toCurrency }}
                </th>
                <th class="text-right">
                  {{ creditCardTotal(items) | toCurrency }}
                </th>
                <th class="text-right">
                  {{ accountTotal(items) | toCurrency }}
                </th>
              </template>
              <template slot="body.append">
                <tr class="red lighten-5 font-weight-black">
                  <th colspan="2"></th>
                  <th>Gross turnover (All)</th>
                  <th colspan="1"></th>
                  <th colspan="3" class="text-right">
                    {{ grossTotal(reportData.items) | toCurrency }}
                  </th>
                </tr>
                <tr class="red lighten-5 font-weight-black">
                  <th colspan="2"></th>
                  <th>% to driver</th>
                  <th colspan="1"></th>
                  <th colspan="3" class="text-right">
                    {{ percentageToDriver(reportData.items) | toCurrency }}
                  </th>
                </tr>
                <tr class="red lighten-5 font-weight-black">
                  <th colspan="2"></th>
                  <th>% to supplier</th>
                  <th colspan="1"></th>
                  <th colspan="3" class="text-right">
                    {{ percentageToSupplier(reportData.items) | toCurrency }}
                  </th>
                </tr>
              </template>
          </v-data-table>
          <div class="pa-4 font-weight-bold text-caption">
            <span class="red--text darken-4">*</span>
            <span class="ml-4">No driver percentage was set for the date.</span>
          </div>
        </v-card>
      </v-container>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import storeConstants from '@/store/storeConstants';
import roleConstants from '@/roleConstants';

export default {
  name: 'DriverPayIn',
  data: () => ({
    reportData: [],
    fromDateMenu: false,
    toDateMenu: false,
    searchCriteria: {
      fromDate: null,
      toDate: null,
      driverId: null,
    },
    entries: [],
    isLoading: false,
    model: null,
    search: null,
    headers: [
      { text: 'Date', value: 'assignedDate' },
      { text: 'Passenger', value: 'passengerName' },
      { text: 'Driver', value: 'driverName' },
      { text: 'Booking #', value: 'bookingId' },
      { text: 'Cash', value: 'cashAmount', align: 'right' },
      { text: 'Credit Card', value: 'creditCardAmount', align: 'right' },
      { text: 'Account', value: 'accountAmount', align: 'right' },
    ],
  }),
  mounted() {
    this.setCriteria();
    if (!this.IS_CONTRACT_DRIVER) {
      this.GET_USERS_BY_ROLE([roleConstants.DRIVER, roleConstants.CONTRACT_DRIVER]);
    }
  },
  methods: {
    ...mapActions([
      storeConstants.GET_REPORT_DRIVER_PAYIN,
      storeConstants.GET_USERS_BY_ROLE,
      storeConstants.SEARCH_DRIVERS]),
    setCriteria() {
      this.setStartOfMonth();
      this.setEndOfMonth();
    },
    runReport() {
      this.GET_REPORT_DRIVER_PAYIN(this.searchCriteria)
        .then((data) => {
          this.reportData = data;
        },
        (error) => {
          this.SET_ALERT_DETAILS({ alertText: error.response.data.error, showAlert: true });
        })
        .catch(() => {
        });
    },
    formatDate(date) {
      if (!date) {
        return null;
      }
      return this.$moment(date).format(this.DISPLAY_DATE_FORMAT);
    },
    setStartOfMonth() {
      const startOfMonth = this.$moment().startOf('month').format(this.ISO_8601_FORMAT);
      this.searchCriteria.fromDate = startOfMonth;
    },
    setEndOfMonth() {
      const endOfMonth = this.$moment(this.searchCriteria.fromDate).endOf('month').format(this.ISO_8601_FORMAT);
      this.searchCriteria.toDate = endOfMonth;
    },
    cashTotal(items) {
      if (!items) {
        return 0;
      }
      return items.reduce((accumulator, object) => accumulator + object.cashAmount, 0);
    },
    driverGrossTotal(items) {
      if (!items) {
        return 0;
      }
      return items.reduce((accumulator, object) => accumulator + this.rowTotal(object), 0);
    },
    creditCardTotal(items) {
      if (!items) {
        return 0;
      }
      return items.reduce((accumulator, object) => accumulator + object.creditCardAmount, 0);
    },
    accountTotal(items) {
      if (!items) {
        return 0;
      }
      return items.reduce((accumulator, object) => accumulator + object.accountAmount, 0);
    },
    grossTotal(items) {
      if (!items) {
        return 0;
      }
      return items.reduce((accumulator, object) => accumulator + this.rowTotal(object), 0);
    },
    rowTotal(item) {
      return (item.accountAmount + item.cashAmount + item.creditCardAmount);
    },
    percentageToSupplier(items) {
      if (!items) {
        return 0;
      }
      return items.reduce((accumulator, object) => accumulator + object.totalSupplierAmount, 0);
    },
    percentageToDriver(items) {
      if (!items) {
        return 0;
      }
      const sum = items.reduce((accumulator, object) => accumulator + object.totalDriverAmount, 0);

      return sum;
    },
    showPercentageNotFoundMark(item) {
      if (!item) {
        return '';
      }
      return !item.percentageFound ? '*' : '';
    },
  },
  computed: {
    ...mapGetters([
      storeConstants.USERS_BY_ROLE,
      storeConstants.SHORT_DATE_FORMAT,
      storeConstants.DISPLAY_DATE_FORMAT,
      storeConstants.ISO_8601_FORMAT,
      storeConstants.IS_CONTRACT_DRIVER]),
    fmtFromDate() {
      return this.formatDate(this.searchCriteria.fromDate);
    },
    fmtToDate() {
      return this.formatDate(this.searchCriteria.toDate);
    },
  },
};

</script>
